import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cdFetch, cdPost } from "../api";
import { queryKeys } from "../queryKeys";
import {
  AllocationSummaryData,
  AllocationUpdatePayload,
  MinAllocationUpdatePayload,
  SmartAllocationTable,
  VolumeAllocationOverallSummary,
} from "../../services/interfaces/IVolumeAllocation";
import { useState } from "react";

const normalize = (input: Array<string> | null) => {
  if (!Array.isArray(input) || input.length === 0) return null;
  return input.slice().sort();
};

const getQueryParams = (
  commodity: string | null | string[],
  district: string | null,
  is_reviewed_flag?: number,
  user_given_allocation?: number | null,
  role_name?: string,
) => {
  let qParams = "";
  if (commodity != null && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }

  if (is_reviewed_flag != undefined && is_reviewed_flag >= 0) {
    qParams += `is_reviewed_flag=${is_reviewed_flag}&`;
  }

  if (
    (user_given_allocation == null || user_given_allocation > 0) &&
    user_given_allocation != undefined
  ) {
    qParams += `user_given_allocation=${user_given_allocation}&`;
  }

  if (role_name) {
    qParams += `role_name=${JSON.stringify(role_name)}&`;
  }

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  return qParams;
};

export const useAllocationFilters = (userEmail: string | null) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.volumeAllocation, "filters", userEmail],
    queryFn: () => getFilters(userEmail),
    enabled:
      userEmail &&
        userEmail !== "" &&
        userEmail !== null &&
        userEmail !== undefined
        ? true
        : false,
  });
  const commodities = data?.data.commodity;
  const date = data?.data.date;
  const date_wire = data?.data.date_wire;
  const filters = { commodities, date, date_wire };
  return { filters, isLoading };
};

const getFilters = (userEmail: string | null) => {
  const res = cdFetch(
    `/getAllFiltersUpcomingMonthlyNegotiations?email=${JSON.stringify(
      userEmail,
    )}`,
  );
  return res;
};

export const useSmartAllocations = (
  commodity: string | null,
  userEmail: string | null,
  trigger: number,
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      queryKeys.volumeAllocation,
      "smart allocation",
      commodity,
      userEmail,
      trigger,
    ],
    queryFn: () => getVolumeAllocation(commodity, userEmail),
    staleTime: 0,
    enabled:
      userEmail &&
        userEmail !== null &&
        userEmail !== "" &&
        userEmail !== undefined
        ? true
        : false,
  });
  const smartAllocations: SmartAllocationTable = data?.data;
  return { smartAllocations, isLoading, refetch };
};

const getVolumeAllocation = (
  commodity: string | null,
  userEmail: string | null,
) => {
  const queryParams = getQueryParams(
    commodity === null ? null : [commodity],
    null,
  );
  const newQueryParam = `${queryParams ? `${queryParams}&` : "?"
    }email=${JSON.stringify(userEmail)}`;
  const res = cdFetch(`/getVolumeAllocationTableData${newQueryParam}`);
  return res;
};

export const useRecommendedVolumeAllocation = (
  commodity: string,
  district: string,
  is_reviewed_flag: number,
  user_given_allocation: number | null,
  role_name: string,
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      queryKeys.volumeAllocation,
      "recommended allocation",
      commodity,
      district,
      is_reviewed_flag,
      user_given_allocation,
    ],
    queryFn: () =>
      getRecommendedAllocations(
        commodity,
        district,
        is_reviewed_flag,
        user_given_allocation,
        role_name,
      ),
    staleTime: 0,
    gcTime: 0,
  });
  const allocationData: AllocationSummaryData = { ...data?.data };
  return { allocationData, isLoading, refetch };
};

const getRecommendedAllocations = (
  commodity: string,
  district: string,
  is_reviewed_flag: number,
  user_given_allocation: number | null,
  role_name: string,
) => {
  const queryParams = getQueryParams(
    commodity,
    district,
    is_reviewed_flag,
    user_given_allocation,
    role_name,
  );
  const res = cdFetch(`/getVolumeAllocationRecommendedData${queryParams}`);
  return res;
};

export const useUpdateVolumeAllocationData = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.volumeAllocation, "allocation"],
    mutationFn: (payload: AllocationUpdatePayload) =>
      updateVolAllocationData(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.volumeAllocation, "allocation"],
      });
      console.log("SUCCESSS", res);
    },
    onError: (error) => {
      console.log("ERROR", error);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  };
};

const updateVolAllocationData = (payload: AllocationUpdatePayload) => {
  const res = cdPost(`/updateVolumeAllocationData`, payload);
  return res;
};

const updateMinVolAllocationData = (payload: MinAllocationUpdatePayload) => {
  const res = cdPost(`/updateMinVolume`, payload);
  return res;
};

export const useAllocationSummary = (
  commodity: string | null,
  userEmail: string | null,
) => {
  const { data: res, isLoading } = useQuery({
    queryKey: [queryKeys.volumeAllocation, "summary", commodity, userEmail],
    queryFn: () => getAllocationSummary(commodity, userEmail),
    staleTime: 0,
    enabled:
      userEmail &&
        userEmail !== null &&
        userEmail !== undefined &&
        userEmail !== ""
        ? true
        : false,
  });

  const data: VolumeAllocationOverallSummary = res?.data;
  return { data, isLoading };
};

const getAllocationSummary = (
  commodity: string | null,
  userEmail: string | null,
) => {
  const queryParams = getQueryParams(commodity, null);
  const newQueryParam = `${queryParams ? `${queryParams}&` : "?"
    }email=${JSON.stringify(userEmail)}`;
  const res = cdFetch(`/getVolumeAllocationSummaryData${newQueryParam}`);
  return res;
};

export const useUpdateMinVolumeAllocation = () => {
  const mutation = useMutation({
    mutationKey: [queryKeys.volumeAllocation, "minVolumeUpdation"],
    mutationFn: (payload: MinAllocationUpdatePayload) =>
      updateMinVolAllocationData(payload),
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isPostLoading: mutation.isPending,
  };
};
